import Service, { type Registry as Services, service } from '@ember/service';
import { task } from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import type {
  BulkApplyBody,
  BulkApplyFilters,
  BulkUserUpdate,
  default as SubscriptionUserAdapter,
} from 'lumen/adapters/subscription-user';
import type { SubscriptionOption } from 'lumen/components/settings/users/subscription-select';
import type {
  BulkApplyChange,
  BulkDeleteChange,
  UserChange,
} from 'lumen/components/settings/users/tables/users-table';
import type ManagedUser from 'lumen/models/managed-user';
import type SubscriptionUser from 'lumen/models/subscription-user';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { ACCESS_RIGHTS, PRODUCTS } from 'lumen/utils/constants/users-constants';

export interface SubscriptionUserMeta {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface SubscriptionUserData {
  meta: SubscriptionUserMeta;
  users: SubscriptionUser[];
}

export interface ManagedUserData {
  meta: SubscriptionUserMeta;
  users: ManagedUser[];
}

export interface RouteParams {
  page?: number;
  product?: string;
  role?: string;
  search?: string;
  sortBy?: string;
  sortDirection?: string;
  subscription?: string;
}

export interface InvitePayload {
  accessRight: string;
  copyReports: boolean;
  emails: string[];
  products: string[];
  subscriptionId: string;
}

/**
 * Holds the data loading and bulk-mutation logic behind the "Settings >
 * Users" page, shared by every context (hotel, market, ...) it's rendered
 * in.
 */
export default class SettingsUsersService extends Service {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  loadManagedUsersTask = task(
    { restartable: true },
    async (params: RouteParams) => {
      try {
        const response = await this.store.query<ManagedUser>('managed-user', {
          namespace: APIGATEWAY,
          typeKey: 'users/me/managed-users',
          // The managed-users endpoint only sorts on `name`, so a `role` sort
          // carried over from the subscription view falls back to `name`.
          sort: `${params.sortDirection === 'desc' ? '-' : ''}name`,
          page: params.page ?? 1,
          ...(params.search ? { 'filter[q]': params.search } : {}),
        });

        return {
          users: [...response],
          meta: response.meta as unknown as SubscriptionUserMeta,
        } satisfies ManagedUserData;
      } catch (error) {
        this.errorTracking.captureException(error);
        await this.alerting.showErrorAlert();
      }
    },
  );

  /**
   * A subscription is only known by its id when landing on the route with a
   * subscription query param, so resolve the option the select needs from the
   * hotel that subscription belongs to.
   */
  loadSubscriptionOptionTask = task(
    { drop: true },
    async (subscriptionId: string): Promise<SubscriptionOption | undefined> => {
      try {
        const hotel =
          this.store.peekRecord<Hotel>('hotel', subscriptionId) ??
          (await this.store.findRecord<Hotel>('hotel', subscriptionId));

        return { id: subscriptionId, label: hotel.hotelinfo.name };
      } catch (error) {
        this.errorTracking.captureException(error);
        return undefined;
      }
    },
  );

  loadUsersTask = task({ restartable: true }, async (params: RouteParams) => {
    try {
      const response = await this.store.query<SubscriptionUser>(
        'subscription-user',
        {
          namespace: APIGATEWAY,
          typeKey: `subscriptions/${params.subscription}/users`,
          sort: `${params.sortDirection === 'desc' ? '-' : ''}${params.sortBy ?? 'name'}`,
          page: params.page ?? 1,
          ...(params.search ? { 'filter[q]': params.search } : {}),
          ...(params.role ? { 'filter[role]': params.role } : {}),
          ...(params.product ? { 'filter[product]': params.product } : {}),
        },
      );

      return {
        users: [...response],
        meta: response.meta as unknown as SubscriptionUserMeta,
      } satisfies SubscriptionUserData;
    } catch (error) {
      this.errorTracking.captureException(error);
      await this.alerting.showErrorAlert();
    }
  });

  async applyBulkChanges(
    subscriptionId: string,
    payload: BulkApplyChange,
    filters: BulkApplyFilters,
  ): Promise<boolean> {
    const confirmed = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('settings.users.bulk_edit.confirm_title'),
      text: this.intl.t('settings.users.bulk_edit.confirm_text', {
        count: payload.selectedCount,
      }),
      unsafe_outsideClickCanCloseAlert: false,
    });

    if (!confirmed) {
      return false;
    }

    try {
      const body: BulkApplyBody = {};
      if (payload.role !== undefined) {
        body.role = payload.role;
      }
      if (payload.addProducts !== undefined) {
        body.add_products = payload.addProducts;
      }
      if (payload.removeProducts !== undefined) {
        body.remove_products = payload.removeProducts;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const adapter = this.store.adapterFor(
        'subscription-user',
      ) as SubscriptionUserAdapter;
      await adapter.bulkApply(subscriptionId, body, {
        excludedUserIds: payload.excludedUserIds,
        filters,
      });

      this.toast.success(
        this.intl.t('settings.users.edit.success', {
          count: payload.selectedCount,
        }),
        { showIcon: true },
      );

      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.edit.failure', {
          count: payload.selectedCount,
        }),
        { showIcon: true },
      );
      return false;
    }
  }

  async bulkDeleteUsers(
    subscriptionId: string,
    payload: BulkDeleteChange,
    filters: BulkApplyFilters,
  ): Promise<boolean> {
    const { excludedUserIds, isSelectAllActive, selectedCount, userIds } =
      payload;

    if (selectedCount === 0) {
      return false;
    }

    const hotelName =
      this.hotelAndMarketService.hotelOrMarket?.hotelinfo?.name ?? '';

    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('settings.users.bulk_delete.confirm_title', {
        count: selectedCount,
      }),
      text: this.intl.t('settings.users.bulk_delete.confirm_message', {
        count: selectedCount,
        hotelName,
      }),
      buttons: {
        cancel: { text: this.intl.t('general.cancel') },
        confirm: { text: this.intl.t('general.confirm') },
      },
    });

    if (!confirmWasClicked) {
      return false;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const adapter = this.store.adapterFor(
        'subscription-user',
      ) as SubscriptionUserAdapter;

      if (isSelectAllActive) {
        await adapter.bulkDeleteAll(subscriptionId, {
          excludedUserIds,
          filters,
        });
      } else {
        await adapter.deleteUsers(subscriptionId, userIds);
      }

      this.toast.success(
        this.intl.t('settings.users.bulk_delete.success', {
          count: selectedCount,
        }),
        { showIcon: true },
      );

      return true;
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.bulk_delete.failure', {
          count: selectedCount,
        }),
        { showIcon: true },
      );
      return false;
    }
  }

  async deleteUser(
    subscriptionId: string,
    user: SubscriptionUser,
    subscriptionLabel: string,
  ): Promise<boolean> {
    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('settings.users.delete.confirm_title'),
      text: this.intl.t('settings.users.delete.confirm_message', {
        hotelName: subscriptionLabel,
      }),
      buttons: {
        cancel: { text: this.intl.t('general.cancel') },
        confirm: { text: this.intl.t('general.confirm') },
      },
    });

    if (!confirmWasClicked) {
      return false;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const adapter = this.store.adapterFor(
        'subscription-user',
      ) as SubscriptionUserAdapter;
      await adapter.deleteUsers(subscriptionId, [Number(user.id)]);

      this.toast.success(this.intl.t('settings.users.delete.success'), {
        showIcon: true,
      });

      return true;
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.delete.failure'), {
        showIcon: true,
      });
      return false;
    }
  }

  async inviteUsers(payload: InvitePayload): Promise<boolean> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const adapter = this.store.adapterFor(
        'subscription-user',
      ) as SubscriptionUserAdapter;
      await adapter.invite(payload.subscriptionId, {
        access_right: payload.accessRight,
        accessible_products: payload.products,
        copy_reports: payload.copyReports,
        emails: payload.emails,
      });

      this.toast.success(this.intl.t('settings.users.invite.success'), {
        showIcon: true,
      });

      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.invite.failure'), {
        showIcon: true,
      });
      return false;
    }
  }

  isAdminForSubscription(subscriptionId: string | null): boolean {
    if (this.access.isStaffUser) {
      return true;
    }

    if (!subscriptionId) {
      return false;
    }

    const relationships = this.hotelAndMarketService.user
      .userhotelrelationships as unknown as Userhotelrelationship[];
    const relationship = relationships.find(
      (rel) => rel.subscriptionId === Number(subscriptionId),
    );

    return relationship?.accessRight === ACCESS_RIGHTS.ADMIN;
  }

  async saveChanges(
    subscriptionId: string,
    changes: UserChange[],
  ): Promise<boolean> {
    const confirmed = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('settings.users.bulk_edit.confirm_title'),
      text: this.intl.t('settings.users.bulk_edit.confirm_text', {
        count: changes.length,
      }),
      buttons: {
        cancel: { text: this.intl.t('general.cancel') },
        confirm: { text: this.intl.t('general.confirm') },
      },
      unsafe_outsideClickCanCloseAlert: false,
    });

    if (!confirmed) {
      return false;
    }

    try {
      const users = changes.map((change) => {
        const update: BulkUserUpdate = { user_id: Number(change.userId) };

        if (change.newRole !== change.originalRole) {
          update.role = change.newRole;
        }

        const hasProductChanged =
          change.newRateAndMarketInsight !==
            change.originalRateAndMarketInsight ||
          change.newBiBenchmarking !== change.originalBiBenchmarking;

        if (hasProductChanged) {
          const products: string[] = [];
          if (change.newRateAndMarketInsight) {
            products.push(PRODUCTS.RATE_INSIGHT);
          }
          if (change.newBiBenchmarking) {
            products.push(PRODUCTS.BI_BENCHMARKING);
          }
          update.products = products;
        }

        return update;
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const adapter = this.store.adapterFor(
        'subscription-user',
      ) as SubscriptionUserAdapter;
      await adapter.bulkUpdate(subscriptionId, users);

      this.toast.success(
        this.intl.t('settings.users.edit.success', { count: changes.length }),
        { showIcon: true },
      );

      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(
        this.intl.t('settings.users.edit.failure', { count: changes.length }),
        { showIcon: true },
      );
      return false;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'settings-users': SettingsUsersService;
  }
}
